'use client'

import Image from 'next/image'
import { useRef, useState } from 'react'

import Button from '@/components/ui/Button'
import Icon from '@/components/ui/icon'
import { cn, loadImage } from '@/lib/utils'
import { Career, JobPage } from '@/types/job-page.type'

type ClientCompProps = {
  jobPages: JobPage[]
  careers: Career[]
}

export default function ClientComp({ careers, jobPages }: ClientCompProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [careerDesc, setCareerDesc] = useState<Career | null>(null)
  const careerRef = useRef<HTMLDivElement>(null)

  const banner = jobPages[0]
  const companyCulture = jobPages[1]
  const department = jobPages[2]
  const whyUs = jobPages[3]
  const aboutUs = jobPages[4]

  const scrollToCareer = () => {
    careerRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleModal = (career: Career) => {
    setIsOpen(true)
    setCareerDesc(career)
  }

  return (
    <div>
      {/* Banner */}
      <div className="relative bg-red h-[250px] lg:h-auto">
        <Image
          src={loadImage('large', banner.job_pages[0].image_id)}
          alt={banner.name}
          width={0}
          height={0}
          sizes="100vw"
          className="w-full h-full object-cover"
          unoptimized
          priority
        />
        <div className="text-white absolute top-0 w-full h-full flex flex-col items-center justify-center bg-black/30">
          <div className="w-[80%] text-center">
            <h2 className="text-xl lg:text-[40px] mb-5 font-bold">{banner.job_pages[0].name}</h2>
            <p className="text-base lg:text-[26px] mb-5">{banner.job_pages[0].description}</p>
            <button onClick={scrollToCareer} className="mx-auto bg-[#fd7e14] hover:bg-[#726c6d] text-lg uppercase py-1.5 px-4 rounded duration-100">
              Các vị trí tuyển dụng
            </button>
          </div>
        </div>
      </div>
      {/* Văn hóa thế giới IC */}
      <div className="max-w-5xl mx-auto py-[50px] text-center">
        <h2 className="text-3xl mb-[30px] font-bold text-textcolor">{companyCulture.name}</h2>
        <div className="w-1/6 border-t border-t-textcolor mx-auto pb-[30px]"></div>
        <div className="grid grid-cols-1 lg:grid-cols-3">
          <div className="flex gap-4 pl-[30px] pt-[30px] pr-[50px] pb-[45px] bg-[#f4f5f9]">
            <Image
              src={loadImage('large', companyCulture.job_pages[0].image_id)}
              alt={companyCulture.job_pages[0].name}
              width={0}
              height={0}
              sizes="100vw"
              className="w-[65px] h-[65px] mr-5"
              priority
            />
            <div className="text-left">
              <p className="text-xl pb-5 font-bold text-textcolor">{companyCulture.job_pages[0].name}</p>
              <p>{companyCulture.job_pages[0].description}</p>
            </div>
          </div>
          <div className="flex gap-4 pl-[30px] pt-[30px] pr-[50px] pb-[45px] bg-[#edeef2]">
            <Image
              src={loadImage('large', companyCulture.job_pages[1].image_id)}
              alt={companyCulture.job_pages[1].name}
              width={0}
              height={0}
              sizes="100vw"
              className="w-[65px] h-[65px] mr-5"
              priority
            />
            <div className="text-left">
              <p className="text-xl pb-5 font-bold text-textcolor">{companyCulture.job_pages[1].name}</p>
              <p>{companyCulture.job_pages[1].description}</p>
            </div>
          </div>
          <div className="flex gap-4 pl-[30px] pt-[30px] pr-[50px] pb-[45px] bg-[#e1e2e6]">
            <Image
              src={loadImage('large', companyCulture.job_pages[2].image_id)}
              alt={companyCulture.job_pages[2].name}
              width={0}
              height={0}
              sizes="100vw"
              className="w-[65px] h-[65px] mr-5"
              priority
            />
            <div className="text-left">
              <p className="text-xl pb-5 font-bold text-textcolor">{companyCulture.job_pages[2].name}</p>
              <p>{companyCulture.job_pages[2].description}</p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="flex gap-4 pl-[30px] pt-[30px] pr-[50px] pb-[45px] bg-[#f4f5f9] lg:bg-[#e1e2e6]">
            <Image
              src={loadImage('large', companyCulture.job_pages[3].image_id)}
              alt={companyCulture.job_pages[3].name}
              width={0}
              height={0}
              sizes="100vw"
              className="w-[65px] h-[65px] mr-5"
              priority
            />
            <div className="text-left">
              <p className="text-xl pb-5 font-bold text-textcolor">{companyCulture.job_pages[3].name}</p>
              <p>{companyCulture.job_pages[3].description}</p>
            </div>
          </div>
          <div className="flex gap-4 pl-[30px] pt-[30px] pr-[50px] pb-[45px] bg-[#e1e2e6] lg:bg-[#f4f5f9]">
            <Image
              src={loadImage('large', companyCulture.job_pages[4].image_id)}
              alt={companyCulture.job_pages[4].name}
              width={0}
              height={0}
              sizes="100vw"
              className="w-[65px] h-[65px] mr-5"
              priority
            />
            <div className="text-left">
              <p className="text-xl pb-5 font-bold text-textcolor">{companyCulture.job_pages[4].name}</p>
              <p>{companyCulture.job_pages[4].description}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Phòng ban */}
      <div className="mx-auto py-[50px] text-center bg-[#f4f5f9]">
        <h2 className="text-3xl mb-[30px] font-bold text-textcolor">{department.name}</h2>
        <div className="w-1/12 border-t border-t-textcolor mx-auto pb-[30px]"></div>
        <div className="max-w-5xl mx-auto flex flex-col lg:flex-row justify-center gap-10">
          {department.job_pages.map((item) => (
            <div key={item.id} className="w-3/4 mx-auto lg:mx-0 lg:w-1/4 relative cursor-pointer hover:scale-125 duration-1000">
              <Image src={loadImage('medium', item.image_id)} alt={item.name} width={0} height={0} sizes="100vw" className="w-full" unoptimized priority />
              <div className="absolute bottom-0 w-full bg-[#fd7e14] text-left text-white py-1 pl-2">{item.name}</div>
            </div>
          ))}
        </div>
      </div>
      {/* vì sao chọn thế giới ic */}
      <div className="max-w-5xl mx-auto py-[50px] text-center">
        <h2 className="text-3xl mb-[30px] font-bold text-textcolor">{whyUs.name}</h2>
        <div className="w-1/6 border-t border-t-textcolor mx-auto pb-[30px]"></div>
        <div className="grid grid-cols-1 lg:grid-cols-3">
          {whyUs.job_pages.map((item) => (
            <div key={item.id}>
              <div>
                <Image
                  src={loadImage('medium', item.image_id)}
                  alt={item.name}
                  width={0}
                  height={0}
                  sizes="100vw"
                  className="block w-[125px] h-[125px] mx-auto"
                  unoptimized
                  priority
                />
              </div>
              <p className="text-lg py-5 font-bold text-textcolor">{item.name}</p>
              <p className="text-base text-textcolor px-[30px]">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Chúng tôi là thế giới ic */}
      <div className="bg-[#f4f5f9]">
        <div className="max-w-5xl mx-auto py-[50px] text-center">
          <h2 className="text-3xl mb-[30px] font-bold text-textcolor">{aboutUs.name}</h2>
          <div className="w-1/6 border-t border-t-textcolor mx-auto pb-[30px]"></div>
        </div>
      </div>
      {/* Vị trí ứng tuyển */}
      <div className="max-w-5xl mx-auto py-[50px] text-center" ref={careerRef}>
        <h2 className="text-3xl mb-[30px] font-bold text-textcolor">Thegioiic đang tuyển dụng</h2>
        <div className="w-1/6 border-t border-t-textcolor mx-auto pb-[30px]"></div>
        <div>
          {careers.map((career) => (
            <div key={career.id} className="flex flex-col mx-2 lg:mx-0 lg:flex-row items-start lg:items-center justify-between py-[20px] border-b border-b-black/30">
              <div className="flex flex-col lg:flex-row items-start lg:items-center w-full lg:w-3/5 justify-between">
                <div
                  className="text-lg text-left text-textcolor font-bold cursor-pointer hover:text-primary duration-200 flex items-center gap-2"
                  onClick={() => handleModal(career)}
                >
                  {career.name}
                  <span className="flex items-center">
                    <Icon icon="tgic icon-chevron-right" className="w-2" />
                    <Icon icon="tgic icon-chevron-right" className="w-2" />
                  </span>
                </div>
                <p className="text-lg my-2 lg:my-0">{career.career_position.name}</p>
              </div>
              <Button href={`/ung-tuyen?career=${career.id}`} target="_blank" primary outline rounded>
                Ứng tuyển
              </Button>
            </div>
          ))}
        </div>
      </div>
      {/* modal miêu tả công việc */}
      {isOpen && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setIsOpen(false)} aria-modal="true" role="dialog">
          {/* Click overlay để đóng */}
          <div
            className={cn(
              'w-full max-w-lg lg:max-w-2xl bg-white rounded-2xl shadow-2xl transform transition-all duration-300',
              isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0',
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative">
              <h3 className="px-6 py-5 bg-gradient-to-r from-gray-50 to-gray-100 text-xl font-bold text-gray-800 text-center border-b rounded-t-2xl">
                {careerDesc?.name || 'Thông tin nghề nghiệp'}
              </h3>

              {/* Nút đóng */}
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false)
                  setCareerDesc(null)
                }}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white shadow-md hover:bg-gray-50 flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors"
                aria-label="Đóng modal"
              >
                <Icon icon="tgic icon-times" className="w-5 h-5" />
              </button>
            </div>

            {/* Nội dung chính */}
            <div className="p-6 max-h-[70vh] overflow-y-auto prose prose-slate career-content" dangerouslySetInnerHTML={{ __html: careerDesc?.description || '' }} />

            {/* Footer */}
            <div className="px-6 py-5 bg-gray-50 border-t rounded-b-2xl flex justify-end gap-3">
              <Button
                onClick={() => {
                  setIsOpen(false)
                  setCareerDesc(null)
                }}
                thegioiicGray
                rounded
                outline
              >
                Đóng
              </Button>

              <Button href={`/ung-tuyen?career=${careerDesc?.id}`} primary rounded className="!px-6 !py-2.5 text-base font-medium">
                Ứng tuyển ngay
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
